import moment from 'moment';
import { Link, useNavigate } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
// icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRotateRight } from '@fortawesome/free-solid-svg-icons';
// API
import { useEnv } from '../../../../context/env-context';
import { useAuth } from '../../../../hooks/useAuth';
import { toast } from 'react-toastify';
import { makeRequest } from '../../../../utils/makeRequest';
// components
import CollapsedPanel from '../../../../components/CollapsedPanel';
import { Spinner } from '../../../../components/Spinner';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import CommonAccordion from '../../../../components/common/CommonAccordion';
import CommonSelectInput from '../../../../components/common/CommonSelectInput';
import CommonTextInput from '../../../../components/common/CommonTextInput';
// forms
import NewFundingConsumerNameForm from './forms/NewFundingConsumerNameForm';
import NewFundingConsumerAggregatorForm from './forms/NewFundingConsumerAggregatorForm';
import NewFundingStatusForm from './forms/NewFundingStatusForm';
import NewFundingPopoverRepaymentModeForm from './forms/NewFundingPopoverRepaymentModeForm';
import NewFundingPopoverCollectionPolicyForm from './forms/NewFundingPopoverCollectionPolicyForm';
// widgets
import NewFundingRedFlagsWidget from './widgets/NewFundingRedFlagsWidget';
import NewFundingWithdrawalsWidget from './widgets/NewFundingWithdrawalsWidget';
import NewFundingRepaymentsWidget from './widgets/NewFundingRepaymentsWidget';
import NewFundingOutstandingAmountWidget from './widgets/NewFundingOutstandingAmountWidget';
import NewFundingBridgePointsWidget from './widgets/NewFundingBridgePointsWidget';
// utils
import { isFundingBlind } from '../../../../utils/fundings';

function NewFundingDetailsPanel({
  collapsedPanels,
  expandPanel,
  funding,
  consumer,
  bridgePointsBalance,
  totalExpectedBalance,
  spinner,
  setSpinner,
  refreshFundingData,
}) {
  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth();
  const { apiOriginAggregator, apiOriginFunding } = useEnv();

  const [aggregator, setAggregator] = useState(null);

  const getAggregator = useCallback(
    async aggregatorId => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = { token, url: `${apiOriginAggregator}/get/${aggregatorId}` };

      try {
        const data = await makeRequest(config);

        setAggregator(data);
      } catch (error) {
        toast.error(error.message);
      }
    },
    [getAccessTokenSilently, apiOriginAggregator],
  );

  useEffect(() => {
    if (consumer?.aggregatorId !== null && consumer?.aggregatorId !== undefined) {
      getAggregator(consumer.aggregatorId);
    }
  }, [getAggregator, consumer?.aggregatorId]);

  const deletelFunding = async () => {
    if (window.confirm('Are you sure?')) {
      setSpinner(true);
      const token = await getAccessTokenSilently();

      if (!token) {
        return;
      }

      const config = {
        token,
        url: `${apiOriginFunding}/delete`,
        method: 'POST',
        data: JSON.stringify({ fundingId: funding.id }),
      };

      try {
        await makeRequest(config);

        toast.success('Advance successfully deleted!');
        navigate('/fundings');
      } catch (error) {
        toast.error(error.message);
      } finally {
        setSpinner(false);
      }
    }
  };

  const setCustomCollectionPolicyField = async (fieldName, fieldValue) => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/set_custom_collection_policy_field`,
      method: 'POST',
      data: JSON.stringify({ fundingId: funding.id, fieldName, fieldValue }),
    };

    try {
      await makeRequest(config);
      await refreshFundingData();

      toast.success('Custom collection policy fields updated!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const saveTransferSettings = async (settingName, value) => {
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/save_funding_setting`,
      method: 'POST',
      data: JSON.stringify({ fundingId: funding.id, settingName, value }),
    };

    try {
      await makeRequest(config);
      await refreshFundingData();

      toast.success('Advance transfer settings updated!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const refresh = async () => {
    setSpinner(true);
    await refreshFundingData();
    setSpinner(false);
  };

  if (collapsedPanels.details) {
    return <CollapsedPanel side="right" onClick={() => expandPanel(0)} />;
  }

  return (
    <Spinner visible={spinner}>
      <div className="h-100">
        <div className="py-2 px-3">
          <div className="d-flex justify-content-between align-items-center">
            <NewFundingConsumerNameForm
              consumerFirstName={consumer.firstName}
              consumerLastName={consumer.lastName}
              consumerId={consumer.id}
            />
            <Button className="bo-common-base-button pb-1" onClick={refresh}>
              <FontAwesomeIcon icon={faArrowRotateRight} size="sm" color="#5946A4" />
            </Button>
          </div>
          <p className="bo-common-text p-0 m-0 pb-1">{consumer.email}</p>
          <NewFundingConsumerAggregatorForm
            consumerAggregatorId={consumer.aggregatorId}
            consumerAggregatorName={aggregator?.name}
          />
        </div>
        <hr className="my-0" />
        <div className="py-2 px-3">
          <div className="d-flex justify-content-between align-items-center gap-3 pb-1">
            <div className="d-flex align-items-center gap-1">
              <p className="bo-common-text-bold p-0 m-0 text-truncate">Advance ID:</p>
              <Link to={`/funding-old/${funding.id}`} className="bo-consumer-link">
                {funding.id}
              </Link>
            </div>
            <NewFundingStatusForm
              fundingId={funding.id}
              fundingStatus={funding.status}
              refreshFunding={refreshFundingData}
              setSpinner={setSpinner}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center gap-3 pb-1">
            <p className="bo-common-text-bold p-0 m-0 text-truncate">Repayment Mode</p>
            <NewFundingPopoverRepaymentModeForm
              fundingId={funding.id}
              fundingRepaymentMode={funding.repaymentMode}
              refreshFunding={refreshFundingData}
            />
          </div>
          <div className="d-flex justify-content-between align-items-center gap-3 pb-1">
            <p className="bo-common-text-bold p-0 m-0 text-truncate">Blind Collection</p>
            <Badge
              className="bo-common-inline-badge"
              bg={
                isFundingBlind(funding.status, funding.repaymentMode, consumer.bankAccountStatus)
                  ? 'danger'
                  : 'success'
              }
            >
              {isFundingBlind(funding.status, funding.repaymentMode, consumer.bankAccountStatus)
                ? 'Yes'
                : 'No'}
            </Badge>
          </div>
          <div className="d-flex justify-content-between align-items-top gap-3 py-2">
            <p className="bo-common-text-bold p-0 m-0">Reason:</p>
            {funding.textualReason ? (
              <p className="bo-common-text p-0 m-0">{funding.textualReason}</p>
            ) : (
              <p className="bo-common-text p-0 m-0 text-secondary">not provided</p>
            )}
          </div>
          <div className="d-flex justify-content-between align-items-top gap-3 pb-1">
            <p className="bo-common-text p-0 m-0">Age</p>
            <p className="bo-common-text p-0 m-0">
              {funding.createTimestamp
                ? moment.utc(funding.createTimestamp).fromNow().replace('ago', '')
                : '---'}
            </p>
          </div>
          <div className="d-flex justify-content-between align-items-top gap-3 pb-1">
            <p className="bo-common-text p-0 m-0">Created on</p>
            <p className="bo-common-text p-0 m-0">
              {funding.createTimestamp
                ? moment.utc(funding.createTimestamp).local().format('MMM DD, YYYY H:mm (UTCZ)')
                : '---'}
            </p>
          </div>
          <div className="d-flex justify-content-between align-items-top gap-3 pb-1">
            <p className="bo-common-text p-0 m-0">Closed on</p>
            <p className="bo-common-text p-0 m-0">
              {funding.closeTimestamp
                ? moment.utc(funding.closeTimestamp).local().format('MMM DD, YYYY H:mm (UTCZ)')
                : '---'}
            </p>
          </div>
          <div className="d-flex justify-content-between align-items-top gap-3">
            <p className="bo-common-text p-0 m-0">Close reason</p>
            <p className="bo-common-text p-0 m-0">{funding.closeReason || '---'}</p>
          </div>
          <div className="d-flex justify-content-between align-items-top pt-2 pb-2">
            <Button className="bo-common-button" onClick={deletelFunding}>
              Delete Advance
            </Button>
          </div>
          <div className="mb-2 d-flex flex-column align-items-center gap-2">
            {funding.flags.length > 0 && (
              <NewFundingRedFlagsWidget fundingRedFlags={funding.flags} />
            )}
            <NewFundingWithdrawalsWidget
              fundingInitialAmount={funding.amount}
              fundingTopups={funding.events.filter(x => x.description === 'Top-up')}
              consumerId={consumer.id}
              refreshFunding={refreshFundingData}
            />
            <NewFundingRepaymentsWidget
              consumerId={consumer.id}
              consumerDefaultPaymentMethod={consumer.defaultPaymentMethod}
              consumerAccountDwollaFundingSourceUrl={consumer.account?.dwollaFundingSourceUrl}
              fundingId={funding.id}
              fundingCurrentAmount={funding.currentAmount}
              fundingRepaymentMode={funding.repaymentMode}
              fundingNextRepaymentDate={funding.nextRepaymentDate}
              fundingMaxRepaymentDate={funding.maxRepaymentDate}
              fundingActualRepaymentTrials={funding.actualRepaymentTrials}
              fundingRescheduleCounter={funding.rescheduleCounter}
              fundingRepayments={funding.events.filter(x => x.description === 'Repayment')}
              refreshFundingData={refreshFundingData}
            />
            <NewFundingOutstandingAmountWidget
              fundingFeesSum={funding.feesSum}
              fundingOutstandingAmount={funding.outstandingAmount}
            />
            {bridgePointsBalance && (
              <NewFundingBridgePointsWidget
                fundingStatus={funding.status}
                bridgePointsBalance={bridgePointsBalance}
                totalExpectedBalance={totalExpectedBalance}
              />
            )}
          </div>
        </div>
        <CommonAccordion
          title="Collection Policy"
          renderRightPart={() => (
            <NewFundingPopoverCollectionPolicyForm
              fundingId={funding.id}
              fundingCollectionPolicyCode={funding.collectionPolicy?.code}
              refreshFundingData={refreshFundingData}
            />
          )}
        >
          <CommonSelectInput
            label="Intra-day collection frequency"
            initialValue={funding.collectionPolicy?.frequency}
            disabled={funding.collectionPolicy?.code !== 'CUSTOM'}
            options={[
              { key: 'ONCE', value: 'ONCE', name: 'Once a day' },
              { key: 'TWICE', value: 'TWICE', name: 'Twice a day' },
              { key: 'HIGH', value: 'HIGH', name: 'High frequency' },
            ]}
            onSave={value => setCustomCollectionPolicyField('frequency', value)}
          />
          <CommonSelectInput
            label="Full repayment condition"
            labelClassName="mt-3"
            initialValue={funding.collectionPolicy?.fullRepaymentCondition}
            disabled={funding.collectionPolicy?.code !== 'CUSTOM'}
            options={[
              { key: 'BALANCE_ONLY', value: 'BALANCE_ONLY', name: 'Balance only' },
              { key: 'PAYCHECK_DEPOSIT', value: 'PAYCHECK_DEPOSIT', name: 'Paycheck deposit' },
              { key: 'BLINDLY_ON_PAYDAY', value: 'BLINDLY_ON_PAYDAY', name: 'Blindly on payday' },
            ]}
            onSave={value => setCustomCollectionPolicyField('fullRepaymentCondition', value)}
          />
          <CommonSelectInput
            label="Partial repayment condition"
            labelClassName="mt-3"
            initialValue={funding.collectionPolicy?.partialRepaymentCondition}
            disabled={funding.collectionPolicy?.code !== 'CUSTOM'}
            options={[
              {
                key: 'PAYCHECK_DEPOSIT',
                value: 'PAYCHECK_DEPOSIT',
                name: 'Paycheck deposit or last cluster trial',
              },
              {
                key: 'LAST_PAYDAY_TRIAL',
                value: 'LAST_PAYDAY_TRIAL',
                name: 'Paycheck deposit or last payday trial',
              },
              { key: 'FIRST_CHANCE', value: 'FIRST_CHANCE', name: 'First time balance allows' },
              {
                key: 'WHENEVER_POSSIBLE',
                value: 'WHENEVER_POSSIBLE',
                name: 'Whenever balance allows',
              },
              { key: 'BLINDLY_ON_PAYDAY', value: 'BLINDLY_ON_PAYDAY', name: 'Blindly on payday' },
            ]}
            onSave={value => setCustomCollectionPolicyField('partialRepaymentCondition', value)}
          />
          <CommonSelectInput
            label="Apply threshold"
            labelClassName="mt-3"
            initialValue={funding.collectionPolicy?.applyThreshold}
            disabled={funding.collectionPolicy?.code !== 'CUSTOM'}
            options={[
              { key: 'ALWAYS', value: 'ALWAYS', name: 'Always' },
              { key: 'NPD_ONLY', value: 'NPD_ONLY', name: 'NPD only' },
              { key: 'NEVER', value: 'NEVER', name: 'Never' },
            ]}
            onSave={value => setCustomCollectionPolicyField('applyThreshold', value)}
          />
          <CommonSelectInput
            label="Apply 3 days prediction"
            labelClassName="mt-3"
            initialValue={funding.collectionPolicy?.applyPrediction}
            disabled={funding.collectionPolicy?.code !== 'CUSTOM'}
            options={[
              { key: 'ALWAYS', value: 'ALWAYS', name: 'Always' },
              { key: 'NPD_ONLY', value: 'NPD_ONLY', name: 'NPD only' },
              { key: 'NEVER', value: 'NEVER', name: 'Never' },
            ]}
            onSave={value => setCustomCollectionPolicyField('applyPrediction', value)}
          />
        </CommonAccordion>
        <CommonAccordion title="Settings">
          <CommonTextInput
            label="Free credit transfers"
            initialValue={funding.freeCreditTransfers}
            type="numeric"
            allowLeadingZeros={false}
            allowEmpty={false}
            onSave={value => saveTransferSettings('freeCreditTransfers', value)}
          />
          <CommonTextInput
            label="Free debit transfers"
            labelClassName="mt-3"
            initialValue={funding.freeDebitTransfers}
            type="numeric"
            allowLeadingZeros={false}
            allowEmpty={false}
            onSave={value => saveTransferSettings('freeDebitTransfers', value)}
          />
          <CommonTextInput
            label="Transfer fee ($)"
            labelClassName="mt-3"
            initialValue={funding.transferFee}
            type="numeric"
            allowLeadingZeros={false}
            allowEmpty={false}
            decimalScale={2}
            onSave={value => saveTransferSettings('transferFee', value)}
          />
          <CommonTextInput
            label="Collection limit ($)"
            labelClassName="mt-3"
            initialValue={funding.collectionLimit}
            type="numeric"
            allowLeadingZeros={false}
            allowEmpty={false}
            onSave={value => saveTransferSettings('collectionLimit', value)}
          />
          <CommonTextInput
            label="Collection balance threshold ($)"
            labelClassName="mt-3"
            initialValue={funding.collectionBalanceThreshold}
            type="numeric"
            allowLeadingZeros={false}
            allowEmpty={false}
            onSave={value => saveTransferSettings('collectionBalanceThreshold', value)}
          />
          <CommonTextInput
            label="Minimal partial collection ($)"
            labelClassName="mt-3"
            initialValue={funding.minimalPartialCollection}
            type="numeric"
            allowLeadingZeros={false}
            allowEmpty={false}
            onSave={value => saveTransferSettings('minimalPartialCollection', value)}
          />
        </CommonAccordion>
      </div>
    </Spinner>
  );
}

export default NewFundingDetailsPanel;
